/**
 * ProductItemRenderer - Unified Shopify Product Tile Renderer
 * Replaces skeleton placeholders with compiled Handlebars templates
 */
class ProductItemRenderer {
    constructor() {
        this.template = null;
        this.init();
    }

    /**
     * Initialize the renderer
     */
    init() {
        // Wait for DOM and Handlebars to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setup());
        } else {
            this.setup();
        }
    }

    /**
     * Setup the template and render all items
     */
    setup() {
        // Check if Handlebars is available
        if (typeof Handlebars === 'undefined') {
            console.error('ProductItemRenderer: Handlebars is not available');
            return;
        }

        // Get the template from the assets file or script tag
        this.loadTemplate().then(() => {
            this.renderAll();
        }).catch(error => {
            console.error('ProductItemRenderer: Failed to load template', error);
        });
    }

    /**
     * Load the Handlebars template
     */
    async loadTemplate() {
        try {
            // Try to get template from a script tag first (for existing implementations)
            const scriptTemplate = document.getElementById('product-item');
            if (scriptTemplate) {
                const templateSource = scriptTemplate.innerHTML;
                this.template = Handlebars.compile(templateSource);
                return;
            }

            // Fallback: load from assets file
            const response = await fetch(window.Shopify?.routes?.root ?
                `${window.Shopify.routes.root}assets/hbs-product-item.liquid` :
                '/assets/hbs-product-item.liquid'
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const templateSource = await response.text();
            this.template = Handlebars.compile(templateSource);
        } catch (error) {
            console.error('ProductItemRenderer: Template loading failed', error);
            throw error;
        }
    }

    /**
     * Render all product items on the page
     */
    renderAll() {
        const elements = document.querySelectorAll('.js-product-item[data-product-json]:not(.rendered)');
        elements.forEach(el => this.render(el));
    }

    /**
     * Render a single product item
     * @param {HTMLElement} el - The element containing product data
     */
    render(el) {
        if (!this.template) {
            console.error('ProductItemRenderer: Template not loaded');
            return;
        }

        try {
            // Parse the JSON data
            const jsonData = el.dataset.productJson;
            if (!jsonData) {
                console.warn('ProductItemRenderer: No product data found');
                return;
            }

            const data = JSON.parse(jsonData);

            // Compile the template with data
            const html = this.template(data);

            // Create a temporary container to parse the HTML
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;

            // Get the first child (the actual product item)
            const newElement = tempDiv.firstElementChild;

            if (newElement) {
                // Copy any additional classes from the original element
                const originalClasses = Array.from(el.classList).filter(cls =>
                    !['js-product-item', 'placeholder', 'animate-pulse'].includes(cls)
                );
                newElement.classList.add(...originalClasses);

                // Replace the placeholder with the rendered content
                el.parentNode.replaceChild(newElement, el);

                // Mark as rendered to avoid re-processing
                newElement.classList.add('rendered');

                // Trigger any lazy loading for images
                this.triggerLazyLoad(newElement);
            }
        } catch (error) {
            console.error('ProductItemRenderer: Render failed for element', el, error);
            // Remove placeholder styling on error to show broken state
            el.classList.remove('placeholder', 'animate-pulse');
            el.classList.add('render-error');
        }
    }

    /**
     * Trigger lazy loading for images in the rendered element
     * @param {HTMLElement} element - The rendered element
     */
    triggerLazyLoad(element) {
        // If using a lazy loading library, trigger it here
        const lazyImages = element.querySelectorAll('.lazyload');
        if (lazyImages.length > 0 && window.lazySizes) {
            // Trigger lazySizes if available
            window.lazySizes.autoSizer.checkElems();
        } else if (lazyImages.length > 0) {
            // Fallback: convert lazyload class to proper loading
            lazyImages.forEach(img => {
                if (img.dataset.src) {
                    img.src = img.dataset.src;
                    img.classList.remove('lazyload');
                    img.classList.add('lazyloaded');
                }
            });
        }
    }

    /**
     * Render new items that may have been added dynamically
     * Useful for AJAX-loaded content
     */
    refresh() {
        this.renderAll();
    }

    /**
     * Destroy the renderer and clean up
     */
    destroy() {
        this.template = null;
    }
}

// Auto-initialize when DOM is ready
if (typeof window !== 'undefined') {
    window.ProductItemRenderer = ProductItemRenderer;

    // Create global instance
    window.productItemRenderer = new ProductItemRenderer();

    // For AJAX compatibility, expose refresh method globally
    window.refreshProductTiles = function () {
        if (window.productItemRenderer) {
            window.productItemRenderer.refresh();
        }
    };
}